import React, { createContext, useState, useContext } from 'react';

/**
 * Gallery object structure
 * @typedef {Object} Gallery
 * @property {string} _id - Unique identifier for the gallery.
 * @property {string} name - Name of the gallery.
 * @property {string} description - Description of the gallery.
 * @property {Date} createdAt - Creation date of the gallery.
 * @property {Date} [updatedAt] - Optional last updated date of the gallery.
 * @property {boolean} isPublic - Indicates if the gallery is public.
 */

/**
 * State structure for gallery data.
 * @typedef {Object} GalleriesState
 * @property {Gallery[]} data - Array of gallery objects.
 * @property {boolean} loading - Loading status for gallery data.
 * @property {number} loadedAt - Timestamp of when the gallery data was last loaded.
 */

/**
 * Account object structure
 * @typedef {Object} Account
 * @property {string} _id - Unique identifier for the account.
 * @property {Date} createdOn - Creation date of the account.
 * @property {Number} storageLimit - The maximum storage in bytes.
 * @property {Number} storageUsed - The amount of storage used in bytes.
 */

/**
 * State structure for account data.
 * @typedef {Object} AccountState
 * @property {Account} data - The account object
 * @property {boolean} loading - Loading status for account data.
 * @property {number} loadedAt - Timestamp of when the account data was last loaded.
 */

/**
 * Context value for AppDataContext.
 * @typedef {Object} AppDataContextValue
 * @property {GalleriesState} galleries - The current galleries state.
 * @property {(state: GalleriesState) => void} setGalleries - Sets the galleries state.
 * @property {GalleriesState} account - The current account state.
 * @property {(state: GalleriesState) => void} setAccount - Sets the account state.
 * @property {(galleryData: Gallery) => void} updateGalleryState - Sets the gallery data to the existing gallery within the gallery array.
 * @property {(galleryID: string) => void} deleteGalleryState - Deletes the gallery with the given ID from the state.
 */

/** @type {React.Context<AppDataContextValue>} */
const AppDataContext = createContext();

/**
 * Context provider for managing gallery data in the application.
 * @param {{ children: React.ReactNode }} props - The children components wrapped by this provider.
 * @returns {JSX.Element} The provider component for managing application gallery data.
 */
export const AppDataProvider = ({ children }) => {
	const initialGalleriesState = { data: [], loading: false, loadedAt: 0 };
	const initialAccountState = { data: undefined, loading: false, loadedAt: 0 };
	const [galleries, setGalleries] = useState(initialGalleriesState);
	const [account, setAccountState] = useState(initialAccountState);

	/**
	 * Sets the gallery data to the existing gallery within the gallery array.
	 * @param {Gallery} galleryData - The updated gallery data.
	 */
	const updateGalleryState = (galleryData) => {
		if (!galleryData || !galleryData._id) return;
		const updatedGalleriesData = galleries.data.map((g) =>
			g._id === galleryData._id ? { ...g, ...galleryData } : g
		);
		setGalleries({ ...galleries, data: updatedGalleriesData });
	};

	/**
	 * Deletes the gallery with the given ID from the state.
	 * @param {string} galleryID - The ID of the gallery to delete.
	 */
	const deleteGalleryState = (galleryID) => {
		const updatedGalleriesData = galleries.data.filter((g) => g._id !== galleryID);
		setGalleries({ ...galleries, data: updatedGalleriesData });
	};

	const setAccount = (account) => {
		setAccountState({ loadedAt: Date.now(), loading: false, data: account });
	};

	return (
		<AppDataContext.Provider
			value={{
				galleries,
				setGalleries: /** @type {(state: GalleriesState) => void} */ (setGalleries),
				updateGalleryState,
				deleteGalleryState,
				account,
				setAccount
			}}
		>
			{children}
		</AppDataContext.Provider>
	);
};

/**
 * Custom hook to access the gallery data context.
 * @returns {AppDataContextValue} The context value containing galleries state and update functions.
 */
export const useAppData = () => useContext(AppDataContext);
